import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { 
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  UserIcon,
  HeartIcon,
  ShoppingCartIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import LanguageSwitcher from './LanguageSwitcher';
import './MobileMenu.css';
import { Category } from '../features/categories/categoryModels';
import { on } from 'events';
interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSearchOpen: () => void;
  categories: Category[];
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, onSearchOpen, categories }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { getItemCount } = useCart();
  const location = useLocation();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [isAnimating, setIsAnimating] = useState(false);
  const sortedCategories = [...categories].sort((a, b) => a?.categoryOrder - b?.categoryOrder);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsAnimating(true);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
    handleClose();
  };

  const handleLinkClick = () => {
    handleClose();
  };

  const renderCategory = (category: any, level: number = 0) => {
    // const hasSubcategories = category.subcategories && category.subcategories.length > 0;
    const isExpanded = expandedCategories.has(category._id);

    return (
      <div key={category.id} className={`mobile-category-item level-${level}`}>
        <div className="mobile-category-row">
                    <Link 
                        key={category._id} 
                        to={`/products?category=${category._id}`} 
                        onClick={() => toggleCategory(category._id)}
                        className={"mobile-category-button"}>
      
            <div className="mobile-category-icon-wrapper">
              <img className="mobile-category-icon" src={category.imagePath} alt={category.categoryName} />
              <span className={`mobile-category-name ${isRTL ? 'mobile-category-name-rtl' : ''}`}>{category.categoryName}</span>
            </div>
            <div className="mobile-category-content">
              
              
              <span className="mobile-category-count">({category.productCount})</span>
            </div>
      
          </Link>
        </div>

      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className={`mobile-menu-overlay ${isAnimating ? 'open' : 'closing'}`}>
      <div className="mobile-menu-panel">
        {/* Header */}
        <div className="mobile-menu-header">
          <div className="mobile-menu-title">
            <h2>{t('navigation.menu')}</h2>
          </div>
          <button 
            className="mobile-menu-close"
            onClick={handleClose}
          >
            <XMarkIcon className="close-icon" />
          </button>
        </div>

        {/* User Actions */}
        <div className="mobile-menu-actions">
          <button 
            className="mobile-action-button"
            onClick={() => { onSearchOpen(); handleClose(); }}
          >
            <MagnifyingGlassIcon className="action-icon" />
            <span>{t('navigation.search')}</span>
          </button>
          
          
          <Link 
            to="/cart" 
            className="mobile-action-button"
            onClick={handleLinkClick}
          >
            <ShoppingCartIcon className="action-icon" />
            <span>{t('navigation.cart')} ({getItemCount()})</span>
          </Link>
        </div>

        {/* Navigation Links */}
        <div className="mobile-menu-nav">
          <Link 
            to="/" 
            className={`mobile-nav-link ${location.pathname === '/' ? 'active' : ''}`}
            onClick={handleLinkClick}
          >
            {t('navigation.home')}
          </Link>
          
          <Link 
            to="/products" 
            className={`mobile-nav-link ${location.pathname.startsWith('/products') ? 'active' : ''}`}
            onClick={handleLinkClick}
          >
            {t('navigation.allProducts')}
          </Link>
          
          <Link 
            to="/categories" 
            className={`mobile-nav-link ${location.pathname === '/categories' ? 'active' : ''}`}
            onClick={handleLinkClick}
          >
            {t('navigation.categories')}
          </Link>
        </div>

        {/* Categories */}
        <div className="mobile-menu-categories">
          <h3 className="mobile-categories-title">{t('mobileMenu.shopByCategory')}</h3>
          <div className="mobile-categories-list">
            {sortedCategories.map(category => renderCategory(category))}
          </div>
        </div>

        {/* Language Switcher */}
        <div className="mobile-menu-language">
          <LanguageSwitcher />
        </div>

        {/* Footer Links */}
        <div className="mobile-menu-footer">
          <Link to="/about" className="mobile-footer-link" onClick={handleLinkClick}>
            {t('mobileMenu.aboutUs')}
          </Link>
          <Link to="/contact" className="mobile-footer-link" onClick={handleLinkClick}>
            {t('mobileMenu.contact')}
          </Link>
          <Link to="/help" className="mobile-footer-link" onClick={handleLinkClick}>
            {t('mobileMenu.helpCenter')}
          </Link>
          <Link to="/shipping" className="mobile-footer-link" onClick={handleLinkClick}>
            {t('mobileMenu.shippingInfo')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
